import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";

import type { Dayjs } from "dayjs";

dayjs.extend(utc);
dayjs.extend(timezone);

/** Current clock hour (0–23) in the given IANA time zone. */
export function getCurrentHourInTimezone(timeZone: string): number {
  const parts = new Intl.DateTimeFormat("en-GB", {
    hour: "numeric",
    hourCycle: "h23",
    timeZone,
  }).formatToParts(new Date());
  const hourPart = parts.find((p) => p.type === "hour");
  return hourPart ? Number.parseInt(hourPart.value, 10) : 0;
}

/** Current minute-of-day (0–1439) in the given IANA time zone. */
export function getCurrentMinuteOfDayInTimezone(timeZone: string): number {
  const parts = new Intl.DateTimeFormat("en-GB", {
    hour: "numeric",
    minute: "numeric",
    hourCycle: "h23",
    timeZone,
  }).formatToParts(new Date());
  const hour = parts.find((p) => p.type === "hour")?.value ?? "0";
  const minute = parts.find((p) => p.type === "minute")?.value ?? "0";
  return Number.parseInt(hour, 10) * 60 + Number.parseInt(minute, 10);
}

export const getLocalQuietHours = (
  quietHourStart: string,
  quietHourEnd: string,
  currentTimezone: string,
) => {
  if (!quietHourStart || !quietHourEnd) {
    return null;
  }

  // Quiet hours are stored as bare UTC hours (re-anchored to 1970-01-01 by
  // `quietHourToIso`). Converting that fixed anchor through `.tz()` would use
  // the 1970 (standard-time) offset, leaving the local window an hour off during
  // DST. Re-anchor the UTC hour onto today so the conversion picks up the
  // current offset — consistent with `getCurrentHourInTimezone`, which reads the
  // wall-clock hour from today's `new Date()`.
  const startHourUtc = dayjs.utc(quietHourStart).hour();
  const endHourUtc = dayjs.utc(quietHourEnd).hour();
  const anchor = dayjs.utc();

  const startLocal = anchor.hour(startHourUtc).tz(currentTimezone).hour();
  const endLocal = anchor.hour(endHourUtc).tz(currentTimezone).hour();

  return { start: startLocal, end: endLocal };
};

/** Generic timezone abbreviation for display, e.g. "ET" (falls back to "EST"). */
function getTimezoneAbbreviation(timeZone: string): string {
  for (const timeZoneName of ["shortGeneric", "short"] as const) {
    try {
      const parts = new Intl.DateTimeFormat("en-US", {
        timeZone,
        timeZoneName,
      }).formatToParts(new Date());
      const name = parts.find((p) => p.type === "timeZoneName")?.value;
      if (name) return normalizeAbbreviation(name);
    } catch {
      // Older runtimes may not support "shortGeneric"; try the next option.
    }
  }
  return "";
}

/**
 * Normalizes the zero-offset abbreviation to plain "GMT". Newer ICU data
 * (e.g. on CI) renders UTC as "GMT+0", while older data renders it as "GMT";
 * both mean the same zone, so display them consistently.
 */
function normalizeAbbreviation(name: string): string {
  return name.replace(/^GMT[+-]0$/, "GMT");
}

/** Bare 12-hour clock time for a clock hour (0–23), e.g. "8:00 PM". */
function formatClockHour(hour: number): string {
  return dayjs().hour(hour).minute(0).format("h:mm A");
}

/**
 * Human-readable clock hour with the site-timezone abbreviation,
 * e.g. "1:00 PM ET". The hour is a site-timezone clock hour (0–23).
 */
export function formatHourInTimezone(hour: number, timeZone: string): string {
  const time = formatClockHour(hour);
  const abbreviation = getTimezoneAbbreviation(timeZone);
  return abbreviation ? `${time} ${abbreviation}` : time;
}

/**
 * Human-readable quiet-hours window in the site timezone,
 * e.g. "8:00 PM – 1:00 PM ET". Null when quiet hours are not configured.
 */
export function formatQuietHoursWindow(
  localQuietHours: { start: number; end: number } | null,
  timeZone: string,
): string | null {
  if (!localQuietHours) {
    return null;
  }
  return `${formatClockHour(localQuietHours.start)} – ${formatHourInTimezone(localQuietHours.end, timeZone)}`;
}

/** Whether a clock hour (0–23) in the site timezone falls in the quiet-hours window. */
export function isHourInLocalQuietHours(
  hour: number,
  localQuietHours: { start: number; end: number } | null,
): boolean {
  if (!localQuietHours) {
    return false;
  }
  const { start, end } = localQuietHours;
  if (start > end) {
    return hour >= start || hour < end;
  }
  return hour >= start && hour < end;
}

/**
 * Returns a :00 or :30 on the same calendar day as `picked` that is outside
 * quiet hours (when configured) and not strictly before `now`.
 *
 * Tries `picked` (snapped to a half-hour) first, then steps forward by 30 minutes
 * so a valid existing time is preserved when the user keeps the time and changes
 * the date. If nothing works from `picked` through end of day, scans from
 * midnight (handles date-only selection defaulting to 12:00 a.m.).
 */
export function getFirstAllowedHalfHourOnSameDay(
  picked: Dayjs,
  now: Dayjs,
  localQuietHours: { start: number; end: number } | null,
): Dayjs {
  const dayKey = picked.format("YYYY-MM-DD");

  const snapToHalfHour = (d: Dayjs): Dayjs => {
    const base = d.second(0).millisecond(0);
    const m = base.minute();
    if (m === 0 || m === 30) {
      return base;
    }
    return base.minute(m < 30 ? 0 : 30);
  };

  const isAllowed = (candidate: Dayjs): boolean => {
    const hour = candidate.hour();
    if (localQuietHours && isHourInLocalQuietHours(hour, localQuietHours)) {
      return false;
    }
    return !candidate.isBefore(now);
  };

  let candidate = snapToHalfHour(picked);

  for (let i = 0; i < 48; i += 1) {
    if (candidate.format("YYYY-MM-DD") !== dayKey) {
      break;
    }
    if (isAllowed(candidate)) {
      return candidate;
    }
    candidate = candidate.add(30, "minute");
  }

  const startOfDay = picked.startOf("day");
  for (let hour = 0; hour < 24; hour += 1) {
    for (const minute of [0, 30] as const) {
      const c = startOfDay.hour(hour).minute(minute).second(0).millisecond(0);
      if (c.format("YYYY-MM-DD") !== dayKey) {
        continue;
      }
      if (localQuietHours && isHourInLocalQuietHours(hour, localQuietHours)) {
        continue;
      }
      if (c.isBefore(now)) {
        continue;
      }
      return c;
    }
  }

  return picked.second(0).millisecond(0);
}
